import {
  IOsiServiceAbonentsButtonFeatureViewModelToken,
  IOsiServiceAbonentsButtonFeatureViewModel
} from '@shared/types/mobx/features/osiAccruals';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import PeopleIcon from '@mui/icons-material/People';
import { LoadingButton } from '@mui/lab';
import React, { MutableRefObject, useEffect, useImperativeHandle } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Slide,
  Typography
} from '@mui/material';
import { useTranslation } from '@shared/utils/i18n';
import {
  DataGridPro,
  GridColDef,
  GridValueGetterParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef
} from '@mui/x-data-grid-pro';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { OsiServiceResponse } from '@shared/types/osi/services';
// Фаза 2: коды типов помещений — для выбора отображаемого номера арендатора
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { QuickSelectors } from '@features/osi/accruals/quickSelectors';
// Фаза 2: delay и logger больше не нужны (убраны parkingCols)
// Фаза 2: ParkingField больше не нужен — паркинг теперь как отдельные абоненты
import { ForceUpdateConfirmDialog } from '@features/osi/accruals/service/add/ui/ForceUpdateConfirmDialog';

interface Props {
  service?: OsiServiceResponse | null;
  reloadCb: () => Promise<void>;
  ref?: MutableRefObject<RefHandle | null>;
  isButtonHidden?: boolean;
}

export interface RefHandle {
  openTableManual: (service: OsiServiceResponse) => void;
}

// @ts-expect-error component not typed
const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// OSI-795: header-чекбокс «выбрать всё» управляет ТОЛЬКО помещениями (все типы кроме ARENDATOR).
// Состояние checked/indeterminate/disabled берётся из vm.premisesSelectorState — не зависит от арендаторов.
// observer нужен, чтобы чекбокс перерисовывался при изменении выбора в сторе.
const PremisesHeaderCheckbox: React.FC<{ vm: IOsiServiceAbonentsButtonFeatureViewModel }> = observer(({ vm }) => {
  const state = vm.premisesSelectorState;

  return (
    <Checkbox
      checked={state.checked}
      indeterminate={state.indeterminate}
      disabled={state.disabled}
      onChange={(e) => (e.target.checked ? vm.selectAllPremises() : vm.unselectAllPremises())}
    />
  );
});

const ComponentWithForwardRef = React.forwardRef<RefHandle, Props>(
  ({ service = null, reloadCb, isButtonHidden = false }, ref) => {
    const vm = useInjection<IOsiServiceAbonentsButtonFeatureViewModel>(IOsiServiceAbonentsButtonFeatureViewModelToken);

    const { t, fieldWithPrefix: fwp, addPrefix, getGridLocale } = useTranslation();

    const apiRef = useGridApiRef();

    useImperativeHandle(ref, () => ({
      openTableManual: async (service: OsiServiceResponse) => {
        vm.service = service;
        await vm.openAbonentsTable();
      }
    }));

    useEffect(() => {
      vm.service = service;
    }, [service, vm]);

    useEffect(() => {
      vm.reloadCb = reloadCb;
    }, [reloadCb, vm]);

    vm.apiRef = apiRef;

    const cols: GridColDef[] = [
      // OSI-795: переопределяем только renderHeader дефолтной колонки чекбоксов (__check__).
      // renderCell приходит из spread GRID_CHECKBOX_SELECTION_COL_DEF — per-row чекбоксы остаются дефолтными.
      { ...GRID_CHECKBOX_SELECTION_COL_DEF, renderHeader: () => <PremisesHeaderCheckbox vm={vm} /> },
      {
        field: 'flat',
        headerName: t('common:flat'),
        flex: 1,
        // Фаза 2: для арендатора показываем flatDisplay (единый номер от backend), иначе сырой flat
        // (makeFlat в этой таблице не применялся). Сортировка по сырому flat сохранена ниже.
        valueGetter: (params: GridValueGetterParams) =>
          params.row?.areaTypeCode === AreaTypeCodes.ARENDATOR
            ? (params.row.flatDisplay ?? params.row.flat)
            : params.row?.flat,
        sortComparator: flatComparator
      },
      {
        field: addPrefix('areaTypeName'),
        headerName: t('common:type'),
        flex: 1
      },
      {
        field: 'name',
        headerName: t('common:owner'),
        flex: 3
      },
      {
        field: 'floor',
        headerName: t('common:floor'),
        flex: 1
      }
    ];

    // OSI-805: последняя колонка зависит от метода начисления услуги (ветвление набора колонок —
    // по образцу прежнего `vm.isParkingService ? [...parkingCols, ...cols] : cols`).
    // Для TARIF_1LIVING начисление считается от числа проживающих, поэтому «Площадь» там ни о чём
    // не говорит и подменяется на «Проживают» (livingFact уже приходит в AbonentOnServiceResponse).
    // Для всех остальных методов набор колонок остаётся прежним.
    const squareCol: GridColDef = {
      field: 'square',
      headerName: t('common:square'),
      flex: 1
    };

    const livingFactCol: GridColDef = {
      field: 'livingFact',
      headerName: t('common:livingFact'),
      flex: 1
    };

    // Фаза 2: parkingCols убраны — парковочные места теперь отдельные абоненты

    return (
      <>
        {!isButtonHidden && (
          <LoadingButton
            variant="outlined"
            startIcon={<PeopleIcon />}
            color={!vm.activeAbonentsCount ? 'secondary' : 'primary'}
            onClick={vm.openAbonentsTable}
            loading={vm.isAbonentsTableLoading}
            disabled={!vm.service?.isActive || vm.service?.isOsiBilling}
          >
            {vm.isAdditionalService ? `${vm.activeAbonentsCount}` : `${vm.activeAbonentsCount} / ${vm.abonentsCount}`}
          </LoadingButton>
        )}
        <Dialog
          open={vm.isDialogOpen}
          maxWidth="xl"
          onClose={vm.cancelEditing}
          // @ts-expect-error component not typed
          TransitionComponent={Transition}
          aria-labelledby="alert-dialog-slide-title"
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle id="alert-dialog-slide-title" sx={{ width: '50vw' }}>
            <Typography noWrap>{`${t('accruals:service')}: ${fwp(
              vm.service ?? {
                nameRu: '',
                nameKz: ''
              },
              'name'
            )}`}</Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Card sx={{ width: '80vw', px: 1, borderBottomRightRadius: 0, borderBottomLeftRadius: 0 }}>
              <DataGridPro
                apiRef={apiRef}
                columns={vm.isTarif1LivingService ? [...cols, livingFactCol] : [...cols, squareCol]}
                rows={vm.abonents?.slice().sort((a, b) => flatComparator(a?.flat, b?.flat))}
                checkboxSelection
                selectionModel={vm.selectedAbonentsIds}
                onSelectionModelChange={(newModel) => {
                  vm.apiRef = apiRef;
                  vm.selectedAbonentsIds = newModel;
                }}
                disableSelectionOnClick
                density="compact"
                sx={{
                  '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  }
                }}
                components={{
                  LoadingOverlay: LinearProgress,
                  Toolbar: QuickSelectors
                }}
                componentsProps={{
                  toolbar: { viewModel: vm }
                }}
                loading={vm.isLoading}
                isRowSelectable={() => !vm.isLoading}
                editMode="cell"
                experimentalFeatures={{ newEditingApi: true }}
                localeText={getGridLocale()}
                rowHeight={35}
                isCellEditable={(params) => vm.selectedAbonentsIds.includes(params.id)}
                // Фаза 2: cellClickHandler убран вместе с parkingCols
              />
            </Card>
          </DialogContent>
          <DialogActions>
            <Button onClick={vm.cancelEditing}>
              <Typography>{t('common:cancel')}</Typography>
            </Button>
            <Button disabled={vm.isLoading} onClick={vm.save}>
              <Typography>{t('common:save')}</Typography>
            </Button>
          </DialogActions>
        </Dialog>
        <ForceUpdateConfirmDialog
          open={vm.isForceUpdateConfirmDialogOpen}
          onClose={vm.closeForceUpdateConfirmDialog}
          onConfirm={vm.confirmForceUpdate}
          message={vm.forceUpdateMessage}
        />
      </>
    );
  }
);

export const OsiServiceAbonentsButton: React.FC<Props> = observer(ComponentWithForwardRef);
